import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Lock } from "lucide-react";
import type { Outlet } from "@/contexts/OutletContext";

export type StaffInviteSubmit = {
  email: string;
  displayName: string;
  outletIds: string[];
};

type InviteDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  outlets: Outlet[];
  /** Set when editing an existing staff member; their email is then locked. */
  editing?: { email: string; displayName: string | null; outletIds: string[] } | null;
  submitting: boolean;
  onSubmit: (values: StaffInviteSubmit) => void;
};

/**
 * Invite a new staff member, or change which outlets an existing one can work
 * in. Outlet choice is a checkbox list rather than a single select because one
 * person commonly covers several branches.
 */
export const StaffInviteDialog = ({
  open,
  onOpenChange,
  outlets,
  editing,
  submitting,
  onSubmit,
}: InviteDialogProps) => {
  const isEditing = !!editing;
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setEmail(editing?.email ?? "");
    setDisplayName(editing?.displayName ?? "");
    setSelected(editing?.outletIds ?? (outlets.length === 1 ? [outlets[0].outlet_id] : []));
  }, [open, editing, outlets]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = emailValid && selected.length > 0 && !submitting;

  const toggle = (outletId: string) =>
    setSelected((prev) =>
      prev.includes(outletId) ? prev.filter((id) => id !== outletId) : [...prev, outletId]
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="admin-modal-content">
        <DialogHeader className="admin-modal-header">
          <DialogTitle className="font-heading">
            {isEditing ? "Manage outlet access" : "Invite a staff member"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Choose which outlets this person can work in. Removing an outlet takes effect immediately."
              : "They'll get an email invitation. Once they accept, they can sign in to Dibs and work across the outlets you pick."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="space-y-2">
            <Label htmlFor="staff-email">Email address</Label>
            <Input
              id="staff-email"
              type="email"
              autoComplete="off"
              placeholder="name@company.com"
              value={email}
              disabled={isEditing || submitting}
              readOnly={isEditing}
              className={isEditing ? "text-muted-foreground" : undefined}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="staff-name">Name (optional)</Label>
            <Input
              id="staff-name"
              placeholder="How they appear in your staff list"
              value={displayName}
              disabled={submitting}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Outlet access</Label>
            <div className="max-h-56 divide-y divide-border overflow-y-auto rounded-md border border-input bg-card">
              {outlets.length === 0 ? (
                <p className="px-3 py-4 text-sm text-muted-foreground">
                  Create an outlet first — staff access is granted per outlet.
                </p>
              ) : (
                outlets.map((outlet) => (
                  <label
                    key={outlet.outlet_id}
                    htmlFor={`outlet-${outlet.outlet_id}`}
                    className="flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors hover:bg-secondary"
                  >
                    <Checkbox
                      id={`outlet-${outlet.outlet_id}`}
                      checked={selected.includes(outlet.outlet_id)}
                      disabled={submitting}
                      className="border-foreground/60 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                      onCheckedChange={() => toggle(outlet.outlet_id)}
                    />
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                      {outlet.outlet_name}
                    </span>
                  </label>
                ))
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Staff can't open billing &amp; plan, connectors, or staff access.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="neutral-action-button standard-action-button"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            className="standard-action-button"
            disabled={!canSubmit}
            onClick={() =>
              onSubmit({
                email: email.trim().toLowerCase(),
                displayName: displayName.trim(),
                outletIds: selected,
              })
            }
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? "Save changes" : "Send invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

type LimitDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planName: string;
  limit: number;
  onUpgrade: () => void;
};

/**
 * Shown instead of the invite form once every seat on the plan is taken. The
 * point is the upgrade path, so Billing & plan is the primary action.
 */
export const StaffSeatLimitDialog = ({
  open,
  onOpenChange,
  planName,
  limit,
  onUpgrade,
}: LimitDialogProps) => {
  const seatWord = limit === 1 ? "seat" : "seats";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="admin-modal-content">
        <DialogHeader className="admin-modal-header">
          <DialogTitle className="font-heading">You've used all your staff seats</DialogTitle>
          <DialogDescription>
            Your {planName} plan includes {limit} staff {seatWord}, and all of them are taken.
            Upgrade to bring more of your team into Dibs.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-3 rounded-md border border-input bg-secondary/60 p-4">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            You can also free up a seat by revoking access for a staff member you no longer
            work with.
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="admin-modal-secondary-button"
            onClick={() => onOpenChange(false)}
          >
            Not now
          </Button>
          <Button type="button" size="sm" className="standard-action-button" onClick={onUpgrade}>
            View plans
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  staffLabel: string;
  outletName: string | null;
  submitting: boolean;
  onConfirm: () => void;
};

/** Revoking is destructive and instant. The staff member is signed out. */
export const StaffRevokeDialog = ({
  open,
  onOpenChange,
  staffLabel,
  outletName,
  submitting,
  onConfirm,
}: ConfirmDialogProps) => {
  const scope = useMemo(
    () => (outletName ? `access to ${outletName}` : "all access"),
    [outletName]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="admin-modal-content">
        <DialogHeader className="admin-modal-header">
          <DialogTitle className="font-heading">Revoke {scope}?</DialogTitle>
          <DialogDescription>
            {staffLabel} loses {scope} straight away. If they're signed in right now, they'll be
            told and signed out. You can invite them again later.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="admin-modal-secondary-button"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="standard-action-button"
            disabled={submitting}
            onClick={onConfirm}
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Revoke access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
